// Synthesized UI sounds via Web Audio.

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!ctx) {
      const AC = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      ctx = new AC();
    }
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

interface Tone {
  freq: number;
  duration: number;
  type?: OscillatorType;
  endFreq?: number;
  volume?: number;
  delay?: number;
}

function playTone(t: Tone): void {
  const c = getCtx();
  if (!c) return;
  const start = c.currentTime + (t.delay ?? 0);
  const dur = t.duration;
  const vol = t.volume ?? 0.06;

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = t.type ?? 'sine';
  osc.frequency.setValueAtTime(t.freq, start);
  if (t.endFreq && t.endFreq > 0) {
    osc.frequency.exponentialRampToValueAtTime(t.endFreq, start + dur);
  }
  // Quick attack, exponential decay so multiple tones don't pile up muddily.
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.linearRampToValueAtTime(vol, start + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

function chord(notes: Tone[]): void {
  for (const n of notes) playTone(n);
}

let enabled = true;

export function setSfxEnabled(v: boolean): void {
  enabled = v;
}

export function isSfxEnabled(): boolean {
  return enabled;
}

function guarded(fn: () => void) {
  return () => {
    if (!enabled) return;
    try {
      fn();
    } catch {
      /* sound is best-effort */
    }
  };
}

export const sfx = {
  // Three ascending tones — soft system-online feel.
  startup: guarded(() =>
    chord([
      { freq: 523.25, duration: 0.09, type: 'sine', volume: 0.05 }, // C5
      { freq: 659.25, duration: 0.09, type: 'sine', delay: 0.08, volume: 0.05 }, // E5
      { freq: 783.99, duration: 0.14, type: 'sine', delay: 0.16, volume: 0.06 }, // G5
    ]),
  ),
  // Short upward chirp — outgoing.
  send: guarded(() =>
    playTone({ freq: 660, endFreq: 1100, duration: 0.07, type: 'sine', volume: 0.04 }),
  ),
  // Short downward chime — incoming.
  receive: guarded(() =>
    chord([
      { freq: 880, duration: 0.08, type: 'sine', volume: 0.04 },
      { freq: 1320, duration: 0.12, type: 'sine', delay: 0.04, volume: 0.04 },
    ]),
  ),
  // Major third up — green tick.
  accept: guarded(() =>
    chord([
      { freq: 659.25, duration: 0.07, type: 'sine', volume: 0.06 }, // E5
      { freq: 987.77, duration: 0.14, type: 'sine', delay: 0.06, volume: 0.07 }, // B5
    ]),
  ),
  // Two-note descent — soft buzz.
  reject: guarded(() =>
    chord([
      { freq: 311.13, duration: 0.07, type: 'triangle', volume: 0.06 }, // Eb4
      { freq: 233.08, duration: 0.12, type: 'triangle', delay: 0.05, volume: 0.07 }, // Bb3
    ]),
  ),
  // Frequency sweep — handing off.
  forward: guarded(() =>
    playTone({ freq: 440, endFreq: 1760, duration: 0.22, type: 'triangle', volume: 0.05 }),
  ),
};
